import styled, { type CSSProperties } from 'styled-components';
import { Show } from '@elfsight/lib-utils';
import { Typography } from '@elfsight/widget-ui';
import { useSettings } from '@wu/widget-core';
import { fieldsStyles } from '../../form/fields-styles';
import { AnimatedNumber } from './animated-number';
import type { ResultProps } from './result';
import { useAdaptiveFontSize } from './use-adaptive-font-size';

export function ResultPrimary({ calculation, formatter, value }: ResultProps) {
  const { fieldStyle } = useSettings();
  const textStyle = fieldsStyles[fieldStyle].heading;
  const { containerRef, textRef, adaptiveFontSize } = useAdaptiveFontSize(
    textStyle,
    value
  );

  const isString = typeof value === 'string';
  const formattedValue = formatter ? formatter(value) : value;
  const resultStyle: CSSProperties = {
    fontSize: isString ? 24 : adaptiveFontSize,
    position: 'absolute',
    inset: 0
  };

  return (
    <PrimaryContainer>
      <Show when={!!calculation.name}>
        <Typography
          textStyle={{ lineHeight: '26px', fontSize: 20, fontWeight: 'bold' }}
        >
          {calculation.name}
        </Typography>
      </Show>

      <Value
        ref={containerRef}
        textStyle={{
          ...textStyle,
          lineHeight: isString ? '30px' : '40px',
          fontSize: isString ? 24 : adaptiveFontSize
        }}
        _withWrap={isString}
      >
        <HiddenValue ref={textRef} style={{ fontSize: textStyle.fontSize }}>
          {formattedValue}
        </HiddenValue>

        <Show when={isString}>
          <Typography
            textStyle={{ lineHeight: 'inherit', fontWeight: 'bold' }}
            style={resultStyle}
          >
            {formattedValue}
          </Typography>
        </Show>

        <Show when={!isString}>
          <AnimatedNumber
            formatter={formatter}
            value={Number(value)}
            style={resultStyle}
          />
        </Show>
      </Value>

      <Show when={!!calculation.caption}>
        <Typography
          textStyle={{ lineHeight: '16px', fontSize: 13 }}
          style={{ opacity: 0.7, marginTop: 8 }}
          dangerouslySetInnerHTML={{ __html: calculation.caption }}
        />
      </Show>
    </PrimaryContainer>
  );
}

const HiddenValue = styled.span`
  display: inline-block;
  opacity: 0;
  pointer-events: none;
`;

const Value = styled(Typography)<{ _withWrap: boolean }>`
  min-width: 0;
  position: relative;

  white-space: ${({ _withWrap }) => (_withWrap ? 'normal' : 'nowrap')};
`;

const PrimaryContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;
